import { useEffect, useMemo, useState, type FormEvent, type ReactElement } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { createClient, deleteClient, getClientHistory, getClients, updateClient } from "../lib/api";
import { readProfilePhotoUrl, resolveProfilePhotoSrc } from "../lib/profilePhoto";
import type { Client, ClientStatus } from "../types";

const emptyClient = {
  firstName: "",
  lastName: "",
  email: "",
  accessPassword: "",
  phone: "",
  sex: "MALE",
  age: 30,
  heightCm: 170,
  currentWeightKg: 70,
  targetWeightKg: 65,
  objective: "",
  notes: "",
  planName: "",
  planStartDate: "",
  planEndDate: "",
};

type IconName =
  | "users"
  | "userPlus"
  | "clock"
  | "target"
  | "search"
  | "filter"
  | "eye"
  | "edit"
  | "trash"
  | "lock"
  | "calendar"
  | "trendUp"
  | "minus"
  | "arrowLeft"
  | "arrowRight"
  | "plus";

function ClientIcon({ name }: { name: IconName }) {
  const common = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };

  const paths: Record<IconName, ReactElement> = {
    users: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>,
    userPlus: <><path d="M15 19v-1a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v1" /><circle cx="8.5" cy="7" r="4" /><path d="M18 8v6" /><path d="M21 11h-6" /></>,
    clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
    target: <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1.4" fill="currentColor" /></>,
    search: <><circle cx="11" cy="11" r="7" /><path d="m20 20-3-3" /></>,
    filter: <><path d="M3 5h18l-7 8v5l-4 2v-7Z" /></>,
    eye: <><path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6Z" /><circle cx="12" cy="12" r="3" /></>,
    edit: <><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></>,
    trash: <><path d="M3 6h18" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /></>,
    lock: <><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></>,
    calendar: <><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4" /><path d="M8 2v4" /><path d="M3 10h18" /></>,
    trendUp: <><path d="M3 17 9 11l4 4 8-8" /><path d="M14 7h7v7" /></>,
    minus: <path d="M5 12h14" />,
    arrowLeft: <path d="M15 6l-6 6 6 6" />,
    arrowRight: <path d="M9 6l6 6-6 6" />,
    plus: <><path d="M12 5v14" /><path d="M5 12h14" /></>,
  };

  return <svg {...common}>{paths[name]}</svg>;
}

const STATUS_LABELS: Record<ClientStatus, string> = {
  PROGRESS: "Activo",
  NEW: "Nuevo",
  STAGNATED: "Pendiente",
  REGRESSION: "Pendiente",
};

const STATUS_TONES: Record<ClientStatus, "active" | "new" | "pending"> = {
  PROGRESS: "active",
  NEW: "new",
  STAGNATED: "pending",
  REGRESSION: "pending",
};

const PLAN_OPTIONS = ["Plan Básico", "Plan Elite", "Plan Performance"];
const STATUS_FILTER_OPTIONS: Array<{ value: "all" | "active" | "new" | "pending"; label: string }> = [
  { value: "all", label: "Todos los estados" },
  { value: "active", label: "Activos" },
  { value: "new", label: "Nuevos" },
  { value: "pending", label: "Pendientes" },
];
type ClientActivity = {
  label: "Check-in" | "Borrador" | "Registro";
  note: string;
  date: string | null;
};

function formatStartDate(input: string | undefined, index: number): string {
  if (input) {
    const parsed = new Date(input);
    if (!Number.isNaN(parsed.getTime())) {
      const day = String(parsed.getDate()).padStart(2, "0");
      const month = String(parsed.getMonth() + 1).padStart(2, "0");
      const year = parsed.getFullYear();
      return `Inicio: ${day}/${month}/${year}`;
    }
  }
  const fallbacks = ["10/05/2024", "20/05/2024", "05/05/2024", "22/05/2024", "15/04/2024"];
  return `Inicio: ${fallbacks[index % fallbacks.length]}`;
}

function normalizePlanName(planName: string | undefined): string {
  const normalized = (planName || "").trim().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

  if (normalized === "plan basico" || normalized === "basico" || normalized === "basic") return "Plan Básico";
  if (normalized === "plan elite" || normalized === "elite") return "Plan Elite";
  if (normalized === "plan performance" || normalized === "performance") return "Plan Performance";

  return "";
}

function pickFirstNamePart(value: string) {
  return value.trim().split(/\s+/)[0] ?? "";
}

function normalizePersonName(value: string) {
  return pickFirstNamePart(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function formatActivityNote(value: string | null) {
  if (!value) return "Sin actividad";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Sin fecha";

  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / 86_400_000);

  if (date.toDateString() === now.toDateString()) {
    return `Hoy, ${date.toLocaleTimeString("es-PE", { hour: "2-digit", minute: "2-digit" })}`;
  }

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) {
    return `Ayer, ${date.toLocaleTimeString("es-PE", { hour: "2-digit", minute: "2-digit" })}`;
  }

  if (diffDays > 0 && diffDays < 7) {
    return `Hace ${diffDays} dias`;
  }

  return date.toLocaleDateString("es-PE", { day: "2-digit", month: "short", year: "numeric" }).replace(".", "");
}

function readDraftSavedAt(clientId: string) {
  return [`checkin-draft-${clientId}`, `assessment-draft-${clientId}`]
    .map((key) => {
      const rawValue = window.localStorage.getItem(key);
      if (!rawValue) return null;

      try {
        const parsed = JSON.parse(rawValue);
        const savedAt = typeof parsed?._savedAt === "string" ? parsed._savedAt : null;
        return savedAt && !Number.isNaN(new Date(savedAt).getTime()) ? savedAt : null;
      } catch {
        return null;
      }
    })
    .filter((value): value is string => Boolean(value))
    .sort((left, right) => new Date(right).getTime() - new Date(left).getTime())[0] ?? null;
}

function pickLatestActivity(client: Client, latestCheckInDate: string | null, latestDraftDate: string | null): ClientActivity {
  const candidates = [
    latestCheckInDate ? { label: "Check-in" as const, date: latestCheckInDate } : null,
    latestDraftDate ? { label: "Borrador" as const, date: latestDraftDate } : null,
  ].filter((item): item is { label: "Check-in" | "Borrador"; date: string } => Boolean(item));

  if (candidates.length > 0) {
    const latest = candidates.sort((left, right) => new Date(right.date).getTime() - new Date(left.date).getTime())[0];
    return { ...latest, note: formatActivityNote(latest.date) };
  }

  return { label: "Registro", date: client.createdAt, note: formatActivityNote(client.createdAt) };
}

export function ClientsPage() {
  const [searchParams] = useSearchParams();
  const [clients, setClients] = useState<Client[]>([]);
  const [activitiesByClient, setActivitiesByClient] = useState<Record<string, ClientActivity>>({});
  const [form, setForm] = useState(emptyClient);
  const [saving, setSaving] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [clientToDelete, setClientToDelete] = useState<Client | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [formError, setFormError] = useState("");
  const [activeTab, setActiveTab] = useState<"all" | "active" | "new" | "pending">("all");
  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState<string>("all");
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const availablePlans = PLAN_OPTIONS;

  useEffect(() => {
    setSearch(searchParams.get("q") ?? "");
  }, [searchParams]);

  const filteredClients = useMemo(() => {
    let list = clients;
    if (activeTab === "active") list = list.filter((c) => c.latestStatus === "PROGRESS");
    if (activeTab === "new") list = list.filter((c) => c.latestStatus === "NEW");
    if (activeTab === "pending") list = list.filter((c) => c.latestStatus === "REGRESSION" || c.latestStatus === "STAGNATED");

    if (planFilter !== "all") {
      list = list.filter((c) => normalizePlanName(c.planName) === planFilter);
    }

    const term = search.trim().toLowerCase();
    if (!term) return list;
    return list.filter((c) =>
      `${c.firstName} ${c.lastName} ${c.objective ?? ""} ${c.email ?? ""}`.toLowerCase().includes(term),
    );
  }, [clients, activeTab, search, planFilter]);

  async function loadClients() {
    const data = await getClients();
    setClients(data);
    setActivitiesByClient(
      Object.fromEntries(data.map((client) => [client.id, pickLatestActivity(client, null, readDraftSavedAt(client.id))])),
    );

    const activityEntries = await Promise.all(
      data.map(async (client) => {
        try {
          const history = await getClientHistory(client.id);
          const latestCheckIn = history.checkIns
            .slice()
            .sort((left, right) => new Date(right.submittedAt).getTime() - new Date(left.submittedAt).getTime())[0];

          return [client.id, pickLatestActivity(client, latestCheckIn?.submittedAt ?? null, readDraftSavedAt(client.id))] as const;
        } catch {
          return [client.id, pickLatestActivity(client, null, readDraftSavedAt(client.id))] as const;
        }
      }),
    );

    setActivitiesByClient(Object.fromEntries(activityEntries));
  }

  useEffect(() => {
    loadClients();
  }, []);

  function openCreateForm() {
    setEditingClient(null);
    setForm(emptyClient);
    setFormError("");
    setCreateOpen(true);
  }

  function openEditForm(client: Client) {
    setEditingClient(client);
    setFormError("");
    setForm({
      firstName: client.firstName,
      lastName: client.lastName,
      email: client.email ?? "",
      accessPassword: "",
      phone: client.phone ?? "",
      sex: client.sex,
      age: client.age,
      heightCm: client.heightCm,
      currentWeightKg: client.currentWeightKg,
      targetWeightKg: client.targetWeightKg ?? client.currentWeightKg,
      objective: client.objective ?? "",
      notes: client.notes ?? "",
      planName: normalizePlanName(client.planName),
      planStartDate: client.planStartDate ? client.planStartDate.slice(0, 10) : "",
      planEndDate: client.planEndDate ? client.planEndDate.slice(0, 10) : "",
    });
    setCreateOpen(true);
  }

  function closeClientForm() {
    setCreateOpen(false);
    setEditingClient(null);
    setForm(emptyClient);
    setFormError("");
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError("");

    const firstName = pickFirstNamePart(form.firstName);
    const lastName = pickFirstNamePart(form.lastName);
    const normalizedFirstName = normalizePersonName(firstName);
    const normalizedLastName = normalizePersonName(lastName);

    if (!normalizedFirstName || !normalizedLastName) {
      setFormError("Ingresa el primer nombre y el primer apellido del cliente.");
      return;
    }

    const duplicatedClient = clients.find(
      (client) =>
        client.id !== editingClient?.id &&
        normalizePersonName(client.firstName) === normalizedFirstName &&
        normalizePersonName(client.lastName) === normalizedLastName,
    );

    if (duplicatedClient) {
      setFormError(`Ya existe un cliente registrado como ${firstName} ${lastName}.`);
      setForm((current) => ({ ...current, firstName, lastName }));
      return;
    }

    setSaving(true);

    try {
      const payload: Record<string, unknown> = {
        ...form,
        firstName,
        lastName,
        age: Number(form.age),
        heightCm: Number(form.heightCm),
        currentWeightKg: Number(form.currentWeightKg),
        targetWeightKg: Number(form.targetWeightKg),
        planName: form.planName || undefined,
        planStartDate: form.planStartDate ? new Date(form.planStartDate).toISOString() : undefined,
        planEndDate: form.planEndDate ? new Date(form.planEndDate).toISOString() : undefined,
      };

      if (editingClient) {
        const { accessPassword, ...editablePayload } = payload;
        await updateClient(editingClient.id, accessPassword ? payload : editablePayload);
      } else {
        await createClient(payload);
      }

      await loadClients();
      closeClientForm();
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : "No se pudo guardar el cliente. Intenta nuevamente.";
      setFormError(message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteClient() {
    if (!clientToDelete) return;
    setDeleting(true);
    setDeleteError("");

    try {
      const deleteId = clientToDelete.id;
      await deleteClient(deleteId);
      setClients((current) => current.filter((client) => client.id !== deleteId));
      setClientToDelete(null);
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : "No se pudo eliminar el cliente. Intenta nuevamente.";
      setDeleteError(message);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <section className="coach-clients-page">
      <header className="coach-clients-hero">
        <div className="coach-clients-hero-copy">
          <p className="coach-clients-eyebrow">Gestion de clientes</p>
          <h1>Clientes</h1>
          <p>Registra nuevos clientes, organiza sus accesos y revisa rapidamente la base activa del coach.</p>
        </div>
        <div className="coach-clients-hero-art" aria-hidden="true">
          <ClientIcon name="users" />
        </div>
      </header>

      <div className="coach-clients-grid">
        <article className="coach-client-card coach-client-list-card">
          <div className="coach-client-list-head">
            <div className="coach-client-panel-head">
              <span className="coach-client-panel-icon"><ClientIcon name="users" /></span>
              <div>
                <h2>Base de clientes</h2>
                <p>Revisa, filtra y da seguimiento a tus clientes.</p>
              </div>
            </div>
            <div className="coach-client-tools">
              <button type="button" className="coach-client-new-button" onClick={openCreateForm}>
                <ClientIcon name="plus" /> Nuevo cliente
              </button>
              <label className="coach-client-search">
                <ClientIcon name="search" />
                <input
                  type="search"
                  placeholder="Buscar cliente..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </label>
              <div className="coach-client-filter-wrap">
                <button
                  type="button"
                  className={`coach-client-filter${planFilter !== "all" || activeTab !== "all" ? " has-active-filter" : ""}`}
                  aria-haspopup="menu"
                  aria-expanded={filterMenuOpen}
                  aria-label="Abrir filtros"
                  title="Filtros"
                  onClick={() => setFilterMenuOpen((value) => !value)}
                >
                  <ClientIcon name="filter" /> <span className="coach-client-filter-label">Filtros</span>
                  {planFilter !== "all" || activeTab !== "all" ? <span className="coach-client-filter-dot" aria-hidden="true" /> : null}
                </button>
                {filterMenuOpen ? (
                  <>
                    <div className="coach-client-filter-backdrop" onClick={() => setFilterMenuOpen(false)} />
                    <div className="coach-client-filter-menu" role="menu">
                      <div className="coach-client-filter-section">
                        <p className="coach-client-filter-title">Estado</p>
                        {STATUS_FILTER_OPTIONS.map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            className={`coach-client-filter-option${activeTab === option.value ? " is-active" : ""}`}
                            onClick={() => {
                              setActiveTab(option.value);
                            }}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                      <div className="coach-client-filter-section">
                        <p className="coach-client-filter-title">Plan asignado</p>
                        <button
                          type="button"
                          className={`coach-client-filter-option${planFilter === "all" ? " is-active" : ""}`}
                          onClick={() => {
                            setPlanFilter("all");
                          }}
                        >
                          Todos los planes
                        </button>
                        {availablePlans.length > 0 ? (
                          availablePlans.map((plan) => (
                            <button
                              key={plan}
                              type="button"
                              className={`coach-client-filter-option${planFilter === plan ? " is-active" : ""}`}
                              onClick={() => {
                                setPlanFilter(plan);
                              }}
                            >
                              {plan}
                            </button>
                          ))
                        ) : (
                          <p className="coach-client-filter-empty">Aún no hay clientes con plan asignado.</p>
                        )}
                      </div>
                      {planFilter !== "all" || activeTab !== "all" ? (
                        <button
                          type="button"
                          className="coach-client-filter-clear"
                          onClick={() => {
                            setPlanFilter("all");
                            setActiveTab("all");
                            setFilterMenuOpen(false);
                          }}
                        >
                          Limpiar filtro
                        </button>
                      ) : null}
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          </div>

          <div className="coach-client-table">
            <div className="coach-client-table-head">
              <span>Cliente</span>
              <span>Objetivo</span>
              <span>Estado</span>
              <span>Plan asignado</span>
              <span>Última actividad</span>
              <span className="coach-client-th-actions">Acciones</span>
            </div>

            {filteredClients.length > 0 ? (
              filteredClients.map((client, index) => {
                const tone = STATUS_TONES[client.latestStatus];
                const statusLabel = STATUS_LABELS[client.latestStatus];
                const planLabel = normalizePlanName(client.planName) || "Sin plan";
                const activity = activitiesByClient[client.id] ?? pickLatestActivity(client, null, readDraftSavedAt(client.id));
                const profilePhotoSrc = resolveProfilePhotoSrc(readProfilePhotoUrl(client));

                return (
                  <div key={client.id} className="coach-client-row">
                    <Link to={`/clients/${client.id}`} className="coach-client-person">
                      <span className={`coach-client-avatar${profilePhotoSrc ? " coach-client-avatar--photo" : ""}`}>
                        {profilePhotoSrc ? (
                          <img
                            src={profilePhotoSrc}
                            alt={`Foto de ${client.firstName} ${client.lastName}`}
                          />
                        ) : (
                          <>{client.firstName[0]}{client.lastName[0]}</>
                        )}
                      </span>
                      <span className="coach-client-person-name">
                        <strong>{client.firstName} {client.lastName}</strong>
                      </span>
                    </Link>
                    <div className="coach-client-objective">
                      {client.objective || "Definir objetivos con el coach"}
                    </div>
                    <div className="coach-client-status">
                      <span className={`coach-client-pill coach-client-pill--${tone}`}>
                        <span className="coach-client-pill-dot" />
                        {statusLabel}
                      </span>
                    </div>
                    <div className="coach-client-plan">
                      <strong>{planLabel}</strong>
                      <span>{formatStartDate(client.createdAt, index)}</span>
                    </div>
                    <div className="coach-client-activity">
                      <span className="coach-client-activity-icon"><ClientIcon name="calendar" /></span>
                      <span className="coach-client-activity-info">
                        <strong>{activity.label}</strong>
                        <small>{activity.note}</small>
                      </span>
                    </div>
                    <div className="coach-client-actions">
                      <Link
                        to={`/clients/${client.id}/portal?tab=dashboard`}
                        className="coach-client-action coach-client-action--view"
                        aria-label={`Ver procesos de ${client.firstName}`}
                      >
                        <ClientIcon name="eye" />
                        <span>Ver procesos</span>
                      </Link>
                      <button
                        type="button"
                        className="coach-client-action coach-client-action--edit"
                        aria-label={`Editar ${client.firstName}`}
                        onClick={() => openEditForm(client)}
                      >
                        <ClientIcon name="edit" />
                        <span>Editar</span>
                      </button>
                      <button
                        type="button"
                        className="coach-client-action coach-client-action--delete"
                        aria-label={`Eliminar ${client.firstName}`}
                        onClick={() => {
                          setDeleteError("");
                          setClientToDelete(client);
                        }}
                      >
                        <ClientIcon name="trash" />
                        <span>Eliminar</span>
                      </button>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="coach-client-empty">
                <strong>Sin clientes</strong>
                <span>Todavia no hay clientes registrados en la base del coach.</span>
              </div>
            )}
          </div>

        </article>
      </div>

      {createOpen ? (
        <div className="coach-client-modal" role="dialog" aria-modal="true" aria-labelledby="coach-client-create-title">
          <div className="coach-client-modal-backdrop" onClick={closeClientForm} />
          <form className="coach-client-modal-card coach-client-create-card" onSubmit={handleSubmit}>
            <div className="coach-client-modal-head">
              <div className="coach-client-panel-head">
                <span className="coach-client-panel-icon"><ClientIcon name={editingClient ? "edit" : "userPlus"} /></span>
                <div>
                  <h2 id="coach-client-create-title">{editingClient ? "Editar cliente" : "Registrar nuevo cliente"}</h2>
                  <p>{editingClient ? "Modifica los datos registrados para este cliente." : "Completa los datos basicos para crear el acceso de tu cliente."}</p>
                </div>
              </div>
              <button type="button" className="coach-client-close" onClick={closeClientForm} aria-label="Cerrar formulario">
                X
              </button>
            </div>

            <div className="coach-client-form-grid">
              <label>
                <span>Nombre</span>
                <input
                  placeholder="Ej. Juan"
                  value={form.firstName}
                  onBlur={(e) => setForm({ ...form, firstName: pickFirstNamePart(e.target.value) })}
                  onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                  required
                />
              </label>
              <label>
                <span>Apellido</span>
                <input
                  placeholder="Ej. Perez"
                  value={form.lastName}
                  onBlur={(e) => setForm({ ...form, lastName: pickFirstNamePart(e.target.value) })}
                  onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                  required
                />
              </label>
              <label>
                <span>Correo</span>
                <input placeholder="ejemplo@correo.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
              </label>
              <label>
                <span>Clave de acceso</span>
                <span className="coach-client-input-icon">
                  <input
                    type="password"
                    placeholder={editingClient ? "Dejar en blanco para no cambiar" : "Mínimo 8 caracteres"}
                    value={form.accessPassword}
                    onChange={(e) => setForm({ ...form, accessPassword: e.target.value })}
                    minLength={8}
                    required={!editingClient}
                  />
                  <ClientIcon name="lock" />
                </span>
              </label>
              <label>
                <span>Teléfono</span>
                <input placeholder="Ej. 555 123 4567" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </label>
              <label>
                <span>Sexo</span>
                <select value={form.sex} onChange={(e) => setForm({ ...form, sex: e.target.value })}>
                  <option value="MALE">Hombre</option>
                  <option value="FEMALE">Mujer</option>
                </select>
              </label>
              <label>
                <span>Objetivo principal</span>
                <select value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value })} required>
                  <option value="">Selecciona objetivo</option>
                  <option value="Definir y tonificar">Definir y tonificar</option>
                  <option value="Bajar grasa">Bajar grasa</option>
                  <option value="Ganar masa muscular">Ganar masa muscular</option>
                  <option value="Mejorar rendimiento">Mejorar rendimiento</option>
                </select>
              </label>
              <label>
                <span>Plan asignado</span>
                <select value={form.planName} onChange={(e) => setForm({ ...form, planName: e.target.value })}>
                  <option value="">Selecciona plan</option>
                  {PLAN_OPTIONS.map((plan) => (
                    <option key={plan} value={plan}>{plan}</option>
                  ))}
                </select>
              </label>
              <label>
                <span>Inicio del plan</span>
                <input
                  type="date"
                  value={form.planStartDate}
                  onChange={(e) => setForm({ ...form, planStartDate: e.target.value })}
                />
              </label>
              <label>
                <span>Fin del plan</span>
                <input
                  type="date"
                  value={form.planEndDate}
                  min={form.planStartDate || undefined}
                  onChange={(e) => setForm({ ...form, planEndDate: e.target.value })}
                />
              </label>
            </div>

            {formError ? <p className="coach-client-delete-error">{formError}</p> : null}

            <button className="coach-client-submit" type="submit" disabled={saving}>
              <ClientIcon name={editingClient ? "edit" : "userPlus"} />
              {saving ? "Guardando..." : editingClient ? "Guardar cambios" : "Crear cliente"}
            </button>
            <p className="coach-client-secure-note">
              <ClientIcon name="lock" /> {editingClient ? "La clave solo se actualiza si escribes una nueva." : "Se generara acceso y perfil inicial para el cliente."}
            </p>
          </form>
        </div>
      ) : null}

      {clientToDelete ? (
        <div className="coach-client-modal" role="dialog" aria-modal="true" aria-labelledby="coach-client-delete-title">
          <div className="coach-client-modal-backdrop" onClick={() => setClientToDelete(null)} />
          <div className="coach-client-modal-card coach-client-delete-card">
            <div className="coach-client-panel-head">
              <span className="coach-client-panel-icon coach-client-panel-icon--danger"><ClientIcon name="trash" /></span>
              <div>
                <h2 id="coach-client-delete-title">Eliminar cliente</h2>
                <p>Seguro que deseas eliminar a {clientToDelete.firstName} {clientToDelete.lastName}? Esta acción no se puede deshacer.</p>
              </div>
            </div>
            {deleteError ? <p className="coach-client-delete-error">{deleteError}</p> : null}
            <div className="coach-client-delete-actions">
              <button
                type="button"
                className="coach-client-delete-cancel"
                onClick={() => {
                  setDeleteError("");
                  setClientToDelete(null);
                }}
                disabled={deleting}
              >
                Cancelar
              </button>
              <button type="button" className="coach-client-delete-confirm" onClick={handleDeleteClient} disabled={deleting}>
                <ClientIcon name="trash" />
                {deleting ? "Eliminando..." : "Sí, eliminar"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
